export { URBANIK_RELEASE } from '@urbanik/api-contracts';
export { createInstitutionalRule, createInstitutionalRuleVersion, setInstitutionalRuleLifecycle, InstitutionalRuleWriteError, } from './institutional-rule-writes.js';
export * from './institutional-act-writes.js';
export * from './institutional-documents.js';
export * from './institutional-register.js';
export * from './institutional-concepts.js';
export * from './entity-foundation.js';
export * from './organizations-admin.js';
export { getDmExceptions, } from './dm-exceptions.js';
export * from './dm-exception-resolution.js';
export * from './member-financial-account.js';
export { DmStagingApiError, getDmStaging, stageDmWorkbook, } from './dm-staging.js';
export { AdminUsersApiError, changeAdminUserStatus, createAdminUser, getAdminUser, getAdminUsers, } from './admin-users.js';
export { getMemberImportReconciliation, MemberImportReconciliationApiError, resolveMemberImportException, } from './member-import-reconciliation.js';
export { getMemberImportDiagnostic, MemberImportDiagnosticApiError, } from './member-import-diagnostic.js';
export { getMemberImportMapping, MemberImportMappingApiError, } from './member-import-mapping.js';
export { getMemberImportPreview, MemberImportPreviewApiError, } from './member-import-preview.js';
export { getMemberImportSimulation, MemberImportSimulationApiError, } from './member-import-simulation.js';
export { executeMemberImport, MemberImportExecutionApiError, } from './member-import-execution.js';
import { isAccessContextResponse, isHealthResponse, isLoginProblem, isLoginSuccessResponse, isRolesListResponse, isSessionResponse, isPermissionMatrixResponse, isMemberTechnicalExplorerResponse, } from '@urbanik/api-contracts';
export class HealthApiUnavailableError extends Error {
    constructor(message = 'La API de URBANIK no está disponible.') {
        super(message);
        this.name = 'HealthApiUnavailableError';
    }
}
export async function getHealth(baseUrl, signal) {
    let response;
    try {
        response = await fetch(`${baseUrl.replace(/\/$/, '')}/health`, {
            headers: { Accept: 'application/json' },
            signal: signal ?? null,
        });
    }
    catch {
        throw new HealthApiUnavailableError();
    }
    let payload;
    try {
        payload = await response.json();
    }
    catch {
        throw new HealthApiUnavailableError('La API devolvió una respuesta inválida.');
    }
    if (!isHealthResponse(payload))
        throw new HealthApiUnavailableError('El contrato de salud no es válido.');
    if (response.status !== 200 && response.status !== 503)
        throw new HealthApiUnavailableError();
    return payload;
}
export class RolesApiError extends Error {
    kind;
    constructor(kind, message) {
        super(message);
        this.kind = kind;
        this.name = 'RolesApiError';
    }
}
export async function getRoles(baseUrl, access, signal) {
    let response;
    try {
        response = await fetch(`${baseUrl.replace(/\/$/, '')}/identity/roles`, {
            headers: {
                Accept: 'application/json',
                'x-urbanik-bootstrap-email': access.email,
                'x-urbanik-bootstrap-key': access.bootstrapKey,
            },
            credentials: 'include',
            signal: signal ?? null,
        });
    }
    catch {
        throw new RolesApiError('unavailable', 'La API de roles no está disponible.');
    }
    if (response.status === 403)
        throw new RolesApiError('forbidden', 'No tienes permiso para consultar roles.');
    let payload;
    try {
        payload = await response.json();
    }
    catch {
        throw new RolesApiError('invalid', 'La API devolvió una respuesta inválida.');
    }
    if (!response.ok)
        throw new RolesApiError('unavailable', 'No pudimos consultar los roles.');
    if (!isRolesListResponse(payload))
        throw new RolesApiError('invalid', 'El contrato de roles no es válido.');
    return payload;
}
export class LoginApiError extends Error {
    kind;
    retryAfterSeconds;
    constructor(kind, message, retryAfterSeconds) {
        super(message);
        this.kind = kind;
        this.retryAfterSeconds = retryAfterSeconds;
        this.name = 'LoginApiError';
    }
}
export async function login(baseUrl, credentials, signal) {
    let response;
    try {
        response = await fetch(`${baseUrl.replace(/\/$/, '')}/identity/login`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify(credentials),
            credentials: 'include',
            signal: signal ?? null,
        });
    }
    catch {
        throw new LoginApiError('unavailable', 'No pudimos conectar con el servidor.');
    }
    let payload;
    try {
        payload = await response.json();
    }
    catch {
        throw new LoginApiError('unavailable', 'El servidor devolvió una respuesta inválida.');
    }
    if (response.ok && isLoginSuccessResponse(payload))
        return payload;
    if (isLoginProblem(payload)) {
        if (payload.code === 'LOGIN_TEMPORARILY_LOCKED')
            throw new LoginApiError('locked', payload.message, payload.retryAfterSeconds);
        throw new LoginApiError('invalid', payload.message);
    }
    throw new LoginApiError('unavailable', 'No pudimos completar el acceso.');
}
export class SessionApiError extends Error {
    kind;
    constructor(kind, message) {
        super(message);
        this.kind = kind;
        this.name = 'SessionApiError';
    }
}
export async function getSession(baseUrl) {
    let response;
    try {
        response = await fetch(`${baseUrl.replace(/\/$/, '')}/identity/session`, {
            headers: { Accept: 'application/json' },
            credentials: 'include',
        });
    }
    catch {
        throw new SessionApiError('unavailable', 'No pudimos recuperar la sesión.');
    }
    const payload = await response.json();
    if (response.status === 401)
        throw new SessionApiError('expired', 'Tu sesión terminó. Ingresa nuevamente.');
    if (!response.ok || !isSessionResponse(payload))
        throw new SessionApiError('unavailable', 'La sesión no pudo verificarse.');
    return payload;
}
export async function logout(baseUrl) {
    const response = await fetch(`${baseUrl.replace(/\/$/, '')}/identity/logout`, {
        method: 'POST',
        credentials: 'include',
    });
    if (!response.ok)
        throw new SessionApiError('unavailable', 'No pudimos cerrar la sesión.');
}
export class PermissionMatrixApiError extends Error {
    kind;
    constructor(kind, message) {
        super(message);
        this.kind = kind;
        this.name = 'PermissionMatrixApiError';
    }
}
export async function getPermissionMatrix(baseUrl, signal) {
    let response;
    try {
        response = await fetch(`${baseUrl.replace(/\/$/, '')}/identity/permissions-matrix`, {
            headers: { Accept: 'application/json' },
            credentials: 'include',
            signal: signal ?? null,
        });
    }
    catch {
        throw new PermissionMatrixApiError('unavailable', 'No pudimos consultar la matriz.');
    }
    if (response.status === 401)
        throw new PermissionMatrixApiError('unauthorized', 'Debes iniciar sesión.');
    if (response.status === 403)
        throw new PermissionMatrixApiError('forbidden', 'No tienes permiso para consultar la matriz.');
    const payload = await response.json();
    if (!response.ok || !isPermissionMatrixResponse(payload))
        throw new PermissionMatrixApiError('unavailable', 'La respuesta de permisos no es válida.');
    return payload;
}
export class AccessContextApiError extends Error {
    kind;
    constructor(kind, message) {
        super(message);
        this.kind = kind;
        this.name = 'AccessContextApiError';
    }
}
export async function getAccessContext(baseUrl, signal) {
    let response;
    try {
        response = await fetch(`${baseUrl.replace(/\/$/, '')}/identity/access`, {
            headers: { Accept: 'application/json' },
            credentials: 'include',
            signal: signal ?? null,
        });
    }
    catch {
        throw new AccessContextApiError('unavailable', 'No pudimos verificar tus permisos.');
    }
    if (response.status === 401)
        throw new AccessContextApiError('unauthorized', 'Debes iniciar sesión.');
    const payload = await response.json();
    if (!response.ok || !isAccessContextResponse(payload))
        throw new AccessContextApiError('unavailable', 'La respuesta de acceso no es válida.');
    return payload;
}
export class MemberExplorerApiError extends Error {
    kind;
    constructor(kind, message) {
        super(message);
        this.kind = kind;
        this.name = 'MemberExplorerApiError';
    }
}
export async function getTechnicalMember(baseUrl, signal) {
    let response;
    try {
        response = await fetch(`${baseUrl.replace(/\\\/$/, '')}/identity/members/technical-fixture`, {
            headers: { Accept: 'application/json' },
            credentials: 'include',
            signal: signal ?? null,
        });
    }
    catch {
        throw new MemberExplorerApiError('unavailable', 'No pudimos consultar el socio técnico.');
    }
    if (response.status === 401)
        throw new MemberExplorerApiError('unauthorized', 'Debes iniciar sesión.');
    if (response.status === 403)
        throw new MemberExplorerApiError('forbidden', 'No tienes permiso para consultar el explorador.');
    let payload;
    try {
        payload = await response.json();
    }
    catch {
        throw new MemberExplorerApiError('invalid', 'El servidor devolvió una respuesta inválida.');
    }
    if (!response.ok)
        throw new MemberExplorerApiError('unavailable', 'No pudimos consultar el socio técnico.');
    if (!isMemberTechnicalExplorerResponse(payload))
        throw new MemberExplorerApiError('invalid', 'El contrato de socio no es válido.');
    return payload;
}
export { getMembers, MembersListApiError, } from './members-list.js';
export { createMember, CreateMemberApiError, } from './member-create.js';
export { getMemberDetail, MemberDetailApiError, } from './member-detail.js';
export { updateMember, UpdateMemberApiError, } from './member-update.js';
export { changeMemberStatus, ChangeMemberStatusApiError } from './member-status.js';
export { ActsApiError, createAct, downloadActDocument, getAct, getActs, uploadActDocument, deleteAct, restoreAct, updateAct, } from './professional-acts.js';
export { getHouseholdMemberPhoto, uploadHouseholdMemberPhoto, removeHouseholdMemberPhoto, HouseholdMemberPhotoApiError, } from './household-member-photo.js';
export { getMemberUpdateAuthorizations, getMemberUpdateCapabilities, createMemberUpdateAuthorization, revokeMemberUpdateAuthorization, MemberUpdateAuthorizationApiError, } from './member-update-authorization.js';
export { analyzeActDocument } from './professional-acts-ai.js';
export { getAdminMemberProfile, getMemberProfileCatalogs, updateAdminMemberProfile, AdminMemberProfileApiError, } from './admin-member-profile.js';
export { getMemberPhoto, uploadMemberPhoto, approveMemberPhoto, rejectMemberPhoto, MemberPhotoApiError, } from './member-photo.js';
export { getMemberAddressLocation, updateMemberAddressLocation, MemberAddressLocationApiError, } from './member-address-location.js';
export { getHouseholdCatalogs, getMemberHouseholds, saveMemberHousehold, MemberHouseholdApiError, } from './member-household.js';
export { ConceptsApiError, createConcept, getConcepts, setConceptActive, updateConcept, } from './concepts.js';
export { RuleSimulationApiError, simulateRules, } from './rule-simulation.js';
export { FixedRulesApiError, createFixedRule, createFixedRuleVersion, getFixedRules, getFixedRuleVersions, setFixedRuleLifecycle, } from './fixed-rules.js';
export { getUserRoleAssignment, replaceUserRoles } from './user-roles.js';
export { changeRoleStatus, createRole, getRoleCatalog, RoleCatalogApiError, updateRole, } from './role-catalog.js';
export { getRolePermissions, replaceRolePermissions } from './role-permissions.js';
export { issueInitialCredential, changeOwnPassword } from './initial-credentials.js';
export { getObligationModel, ObligationModelApiError, } from './obligation-model.js';
export { createIndividualObligation, IndividualObligationApiError, } from './individual-obligations.js';
export { previewObligationBatch, ObligationBatchPreviewApiError, } from './obligation-batch-preview.js';
export { confirmObligationBatch, ObligationBatchConfirmationApiError, } from './obligation-batch-confirmation.js';
export { getObligationPortfolio, ObligationPortfolioApiError, } from './obligation-portfolio.js';
export { getMemberNumber, assignMemberNumber, transferMemberAliquot, acquireAvailableAliquot, } from './member-number.js';
export { getResignationDocuments, uploadResignationDocument, downloadResignationDocument, changeResignationDocument, } from './resignation-documents.js';
export { getMemberAdmissionPayments, recordMemberAdmissionPayment, verifyMemberAdmission, previewAdmissionRecalculation, confirmAdmissionRecalculation, downloadMemberEntryReceipt, } from './member-admission.js';
export { downloadPrefilledAdmissionTemplate, previewAdmissionImport, confirmAdmissionImport, getRemainingAdmissionVerification, verifyRemainingAdmissions, } from './member-admission-bulk.js';
export { getMemberSelfBalance, provisionMemberAccounts, } from './member-self-service.js';
export { getPaymentDraftCatalogs, getManualAllocationPayments, getManualAllocationDetail, saveManualAllocations, confirmManualAllocations, rejectManualPayment, reversePayment, getPaymentHistory, getPaymentHistoryDetail, paymentReceiptUrl, emailPaymentReceipt, manualAllocationEvidenceUrl, getOwnPaymentDrafts, submitOwnPaymentDraft, searchPaymentMembers, getPaymentDestinationAccounts, createPaymentDestinationAccount, updatePaymentDestinationAccount, } from './member-payment-drafts.js';
export { downloadMemberImportTemplate, previewMemberImportFile, executeMemberImportFile, } from './member-import-execution.js';
